import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { connect } from 'react-redux';

import { SelectSingle } from '../../components/form';
import { BrowserTabTitle, VersionHistory, LoadingOverlay } from '../../components/display';
import { Socket } from '../../components/ws';
import { Button } from '../../components/custom-essentials';
import { Table } from '../../components/table';
import { DetailsModal } from '../../components/modal';
import { formatDateApi, convertApiToDate, getDateObject } from '../../components/functions';
import { checkResponses } from '../../components/form';
import { fetchMemberAppointmentsParentStudent, fetchMemberAppointmentsUnified, fetchMemberStudentsRelevant, fetchMemberStudentsUnified, fetchMemberLearningPlansStudent } from '../../actions';
import Cookies from 'universal-cookie';
import jwt_decode from 'jwt-decode';
import OverduePayments from '../../components/member/OverduePayments';
import PaymentHistory from '../../components/member/PaymentHistory';
import LearningPlanDisplay from '../../components/member/LearningPlanDisplay';
import BookListDisplay from '../../components/member/BookListDisplay';

const cookies = new Cookies();

const versionOptions = [
    { value: 'Admin 4.1', label: 'Admin 4.1' },
    { value: 'Admin 4.0', label: 'Admin 4.0' },
];

const pageTitle = 'Home';

// Member appointments table columns
const getMemberAppointmentColumns = (handleShowModal) => [
    {
        dataField: 'date_time',
        text: 'Date & Time',
        sort: true,
        formatter: (row) => {
            if (!row || !row.date_time) return '-';
            const date = new Date(row.date_time);
            return date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
            });
        }
    },
    {
        dataField: 'student',
        text: 'Student',
        sort: true,
        formatter: (row) => {
            if (!row) return '-';
            return row.studentName || row.student || '-';
        }
    },
    {
        dataField: 'center',
        text: 'Center',
        sort: true,
        formatter: (row) => {
            if (!row) return '-';
            return row.centerName || row.center || '-';
        }
    },
    {
        dataField: 'duration',
        text: 'Duration',
        sort: true,
        formatter: (row) => {
            if (!row || !row.duration) return '-';
            return `${row.duration} min`;
        }
    },
    {
        dataField: 'status',
        text: 'Status',
        sort: true,
        formatter: (row) => {
            if (!row || !row.status) return '-';
            const statusColors = {
                'Scheduled': 'text-mpBlue',
                'In Progress': 'text-mpOrange',
                'Completed': 'text-mpGreen',
                'Missed': 'text-mpLRed',
                'Cancelled': 'text-gray-700'
            };
            const colorClass = statusColors[row.status] || '';
            return React.createElement('span', { className: colorClass }, row.status);
        }
    },
    {
        dataField: 'session_notes',
        text: 'Session Notes',
        formatter: (row) => {
            if (!row || !row.session_notes) return '-';
            const notes = row.session_notes;
            return notes.length > 50 ? `${notes.substring(0, 50)}...` : notes;
        }
    },
    {
        dataField: 'actions',
        text: 'Actions',
        formatter: (row) => {
            if (!row) return null;
            return React.createElement(Button, {
                color: "lte-mpTeal",
                onClick: () => handleShowModal('details', row)
            }, 'View Details');
        }
    }
];

function Home(props) {
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    }, []);

    const [viewVersion, setViewVersion] = useState(versionOptions[0]);
    const [loading, setLoading] = useState(false);
    const [appointments, setAppointments] = useState([]);
    const [students, setStudents] = useState([]);
    const [readingPlexData, setReadingPlexData] = useState({ books: [], bookLists: [], bookListItems: [] }); // For ReadingPlex book data
    const [modalMode, setModalMode] = useState(null);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [apiError, setApiError] = useState(false);
    const [showAllLessons, setShowAllLessons] = useState({}); // Track which students have lessons shown
    const [studentLearningPlans, setStudentLearningPlans] = useState({}); // Learning plans by student ID
    const [expandedCompletedLPs, setExpandedCompletedLPs] = useState({}); // Track which completed LPs are expanded
    // Unified portal state
    const [unifiedData, setUnifiedData] = useState({
        mathplex: { students: [], appointments: [] },
        readingplex: { students: [], appointments: [], books: [], bookLists: [], bookListItems: [] }
    });
    const [hasBothPortals, setHasBothPortals] = useState(false);
    // Toggle for switching between MathPlex and ReadingPlex view
    const [selectedProgramView, setSelectedProgramView] = useState('mathplex'); // 'mathplex' or 'readingplex'

    // Detect portal from domain
    const hostname = window.location.hostname;
    const isReadingPlex = hostname.includes('readingplex');
    const portalName = isReadingPlex ? 'ReadingPlex' : 'MathPlex';

    // Check permissions based on portal - ReadingPlex uses rpPermissions, MathPlex uses mpPermissions
    // Fallback to reading from token if Redux state isn't available
    const getRelevantPermissions = () => {
        const fromRedux = isReadingPlex
            ? (props.auth?.rpPermissions || props.auth?.permissions)
            : (props.auth?.mpPermissions || props.auth?.permissions);
        if (fromRedux) return fromRedux;

        // Try reading from token
        try {
            const aToken = cookies.get('aToken');
            if (aToken) {
                const token = jwt_decode(aToken);
                return isReadingPlex
                    ? (token.rpPermissions || token.mpPermissions)
                    : (token.mpPermissions || token.rpPermissions);
            }
        } catch (error) {
            console.error('Error reading permissions from token:', error);
        }
        return null;
    };

    const relevantPermissions = getRelevantPermissions();
    const isMember = relevantPermissions === 'Member' || relevantPermissions === 'Parent';

    // Check if user has access to both portals
    const checkBothPortals = () => {
        try {
            const aToken = cookies.get('aToken');
            if (aToken) {
                const token = jwt_decode(aToken);
                const hasMP = token.mpPermissions === 'Parent' || token.mpPermissions === 'Member';
                const hasRP = token.rpPermissions === 'Parent' || token.rpPermissions === 'Member';
                return hasMP && hasRP;
            }
        } catch (error) {
            console.error('Error checking both portals:', error);
        }
        return false;
    };

    const userHasBothPortals = checkBothPortals();

    // Fetch member appointments - unified or single portal
    const refreshMemberData = useCallback(async () => {
        if (!isMember) return;

        // Get userId from auth state or token
        let userId = props.auth?.userId;

        // If not in auth state, try to get from token
        if (!userId) {
            try {
                const Cookies = (await import('universal-cookie')).default;
                const cookies = new Cookies();
                const aToken = cookies.get('aToken');
                if (aToken) {
                    const jwt_decode = (await import('jwt-decode')).default;
                    const token = jwt_decode(aToken);
                    userId = token.userId;
                }
            } catch (error) {
                console.error('Error getting userId from token:', error);
            }
        }

        if (!userId) {
            if (mounted.current) {
                setApiError('Unable to identify user. Please log in again.');
                setLoading(false);
            }
            return;
        }

        if (mounted.current) setLoading(true);

        try {
            // Use unified functions if user has both portals, otherwise use single portal
            if (userHasBothPortals) {
                // Fetch unified data
                const [appointmentsRes, studentsRes] = await Promise.all([
                    props.fetchMemberAppointmentsUnified({ userId }),
                    props.fetchMemberStudentsUnified({})
                ]);

                if (mounted.current) {
                    const mpStudents = studentsRes?.data?.mathplex || [];
                    const rpData = studentsRes?.data?.readingplex || { students: [], books: [], bookLists: [], bookListItems: [] };
                    const rpStudents = rpData.students || [];

                    const mpAppointments = appointmentsRes?.data?.mathplex || [];
                    const rpAppointments = appointmentsRes?.data?.readingplex || [];

                    // Combine all students with program tags
                    const allStudents = [
                        ...mpStudents.map(s => ({ ...s, program: 'MathPlex' })),
                        ...rpStudents.map(s => ({ ...s, program: 'ReadingPlex' }))
                    ];

                    // Combine all appointments with program tags
                    const allAppointments = [
                        ...mpAppointments.map(a => ({ ...a, program: 'MathPlex' })),
                        ...rpAppointments.map(a => ({ ...a, program: 'ReadingPlex' }))
                    ];

                    // Create student name map
                    const studentNameMap = {};
                    allStudents.forEach(s => {
                        studentNameMap[s.user_id] = `${s.first_name} ${s.last_name}`;
                    });

                    // Add student names to appointments
                    const appointmentsWithNames = allAppointments.map(apt => ({
                        ...apt,
                        studentName: studentNameMap[apt.student] || `Student ${apt.student.substring(0, 8)}...`
                    }));

                    setUnifiedData({
                        mathplex: { students: mpStudents, appointments: mpAppointments },
                        readingplex: {
                            students: rpStudents,
                            appointments: rpAppointments,
                            books: rpData.books || [],
                            bookLists: rpData.bookLists || [],
                            bookListItems: rpData.bookListItems || []
                        }
                    });
                    setStudents(allStudents);
                    setAppointments(appointmentsWithNames);
                    setReadingPlexData({
                        books: rpData.books || [],
                        bookLists: rpData.bookLists || [],
                        bookListItems: rpData.bookListItems || []
                    });
                    setHasBothPortals(true);

                    // Fetch learning plans for each MathPlex student
                    const lpMap = {};
                    for (const student of mpStudents) {
                        try {
                            const lpRes = await props.fetchMemberLearningPlansStudent({ studentId: student.user_id });
                            if (lpRes && lpRes.status === 200 && lpRes.data) {
                                lpMap[student.user_id] = lpRes.data;
                            }
                        } catch (err) {
                            // Silently continue if LP fetch fails for a student
                        }
                    }
                    if (mounted.current) {
                        setStudentLearningPlans(lpMap);
                    }

                    setApiError(false);
                    setLoading(false);
                }
            } else {
                // Single portal mode (existing logic)
                const appointmentsRes = await props.fetchMemberAppointmentsParentStudent({ userId });

                if (!appointmentsRes || appointmentsRes.status !== 200) {
                    if (mounted.current) {
                        setApiError('Error fetching appointments. Please try again later.');
                        setLoading(false);
                    }
                    return;
                }

                let newStudents = [];
                try {
                    const studentsRes = await props.fetchMemberStudentsRelevant({});
                    if (studentsRes && studentsRes.status === 200) {
                        if (isReadingPlex && studentsRes.data && studentsRes.data.students) {
                            newStudents = studentsRes.data.students || [];
                            if (mounted.current) {
                                setReadingPlexData({
                                    books: studentsRes.data.books || [],
                                    bookLists: studentsRes.data.bookLists || [],
                                    bookListItems: studentsRes.data.bookListItems || []
                                });
                            }
                        } else if (!isReadingPlex && Array.isArray(studentsRes.data)) {
                            newStudents = studentsRes.data || [];
                            if (mounted.current) {
                                setReadingPlexData({ books: [], bookLists: [], bookListItems: [] });
                            }
                        }
                    }
                } catch (err) {
                    // Students fetch failed - continue with appointments only
                }

                const newAppointments = appointmentsRes.data || [];
                const filteredAppointments = newAppointments.filter(apt => {
                    if (isReadingPlex) {
                        return !apt.n_pages && !apt.n_lessons && !apt.stamps_given;
                    } else {
                        return !apt.homework_notes && !apt.participation && !apt.answer_accuracy && !apt.depth_of_thinking;
                    }
                });

                // Add program field for consistency (even in single-portal mode)
                const studentsWithProgram = newStudents.map(s => ({
                    ...s,
                    program: isReadingPlex ? 'ReadingPlex' : 'MathPlex'
                }));

                const appointmentsWithProgram = filteredAppointments.map(apt => ({
                    ...apt,
                    program: isReadingPlex ? 'ReadingPlex' : 'MathPlex'
                }));

                const studentNameMap = {};
                studentsWithProgram.forEach(s => {
                    studentNameMap[s.user_id] = `${s.first_name} ${s.last_name}`;
                });

                const appointmentsWithNames = appointmentsWithProgram.map(apt => ({
                    ...apt,
                    studentName: studentNameMap[apt.student] || `Student ${apt.student.substring(0, 8)}...`
                }));

                if (mounted.current) {
                    setAppointments(appointmentsWithNames);
                    setStudents(studentsWithProgram);
                    setHasBothPortals(false);

                    // Fetch learning plans for each student (MathPlex only)
                    if (!isReadingPlex) {
                        const lpMap = {};
                        for (const student of studentsWithProgram) {
                            try {
                                const lpRes = await props.fetchMemberLearningPlansStudent({ studentId: student.user_id });
                                if (lpRes && lpRes.status === 200 && lpRes.data) {
                                    lpMap[student.user_id] = lpRes.data;
                                }
                            } catch (err) {
                                // Silently continue if LP fetch fails
                            }
                        }
                        if (mounted.current) {
                            setStudentLearningPlans(lpMap);
                        }
                    }

                    setApiError(false);
                    setLoading(false);
                }
            }
        } catch (error) {
            console.error('Error fetching member data:', error);
            if (mounted.current) {
                setApiError('Error fetching data. Please try again later.');
                setLoading(false);
            }
        }
    }, [isMember, props.auth?.userId, userHasBothPortals, props.fetchMemberAppointmentsUnified, props.fetchMemberAppointmentsParentStudent, props.fetchMemberStudentsUnified, props.fetchMemberStudentsRelevant, isReadingPlex]);

    useEffect(() => {
        if (isMember) {
            refreshMemberData();
        }
    }, [isMember, refreshMemberData]);



    const handleShowModal = useCallback((mode, appointment) => {
        if (mounted.current) {
            setModalMode(mode);
            setSelectedAppointment(appointment);
        }
    }, []);

    const onSubmitCallback = useCallback((changes = false) => {
        if (mounted.current) {
            setModalMode(null);
            setSelectedAppointment(null);
            if (changes) refreshMemberData();
        }
    }, [refreshMemberData]);

    // Compute filtering values at top level (before conditional render)
    // Check if user actually has students in both programs
    const hasMathPlexStudents = students.some(s => s.program === 'MathPlex');
    const hasReadingPlexStudents = students.some(s => s.program === 'ReadingPlex');
    const actuallyHasBothPrograms = hasMathPlexStudents && hasReadingPlexStudents;

    // Always show ALL students - filtering is done on appointments/stats, not students
    // Students can be in both programs, so we always show all students
    const filteredStudents = useMemo(() => {
        // Filter students by selected program view
        if (!isMember || !hasBothPortals) {
            return students;
        }

        const targetProgram = selectedProgramView === 'mathplex' ? 'MathPlex' : 'ReadingPlex';
        return students.filter(s => s.program === targetProgram);
    }, [students, selectedProgramView, isMember, hasBothPortals]);

    const filteredAppointments = useMemo(() => {
        // Filter appointments by selected program view
        if (!isMember || !hasBothPortals) {
            return appointments;
        }

        const targetProgram = selectedProgramView === 'mathplex' ? 'MathPlex' : 'ReadingPlex';
        const studentIds = new Set(students.filter(s => s.program === targetProgram).map(s => s.user_id));

        return appointments.filter(a => {
            return a.program === targetProgram && studentIds.has(a.student);
        });
    }, [appointments, selectedProgramView, students, isMember, hasBothPortals]);

    // Member portal view
    if (isMember) {

        // Group appointments by student and separate completed vs upcoming
        // This recalculates on every render when filteredStudents or filteredAppointments change
        const studentAppointmentsMap = {};
        const studentNameMap = {};
        const studentStatsMap = {}; // For calculating totals

        filteredStudents.forEach(student => {
            const studentId = student.user_id;
            studentNameMap[studentId] = `${student.first_name} ${student.last_name}`;
            studentAppointmentsMap[studentId] = {
                completed: [],
                upcoming: []
            };
            studentStatsMap[studentId] = {
                totalPages: 0,
                totalLessons: 0,
                totalStamps: 0,
                completedLessons: [] // Store lessons with dates
            };
        });

        filteredAppointments.forEach(apt => {
            const studentId = apt.student;
            // All students are in the map, so just check if appointment belongs to a valid student
            if (!studentAppointmentsMap[studentId]) {
                return; // Skip if student not found
            }

            // filteredAppointments is already filtered by program, so we can process all of them
            const now = new Date();
            const aptDate = new Date(apt.date_time);
            const isCompleted = ['Completed', 'Missed', 'Cancelled'].includes(apt.status);
            const isUpcoming = aptDate > now && apt.status === 'Scheduled';

            if (isCompleted) {
                studentAppointmentsMap[studentId].completed.push(apt);
                // Calculate stats only for MathPlex appointments
                if (apt.program === 'MathPlex') {
                    if (apt.n_pages) studentStatsMap[studentId].totalPages += parseInt(apt.n_pages) || 0;
                    if (apt.n_lessons) {
                        const lessons = parseInt(apt.n_lessons) || 0;
                        studentStatsMap[studentId].totalLessons += lessons;
                        // Store lesson info for display
                        for (let i = 0; i < lessons; i++) {
                            studentStatsMap[studentId].completedLessons.push({
                                date: apt.date_time,
                                appointmentId: apt.id,
                                sessionNotes: apt.session_notes
                            });
                        }
                    }
                    if (apt.stamps_given) studentStatsMap[studentId].totalStamps += parseInt(apt.stamps_given) || 0;
                }
            } else if (isUpcoming) {
                studentAppointmentsMap[studentId].upcoming.push(apt);
            }
        });

        // Sort completed by most recent first
        Object.keys(studentAppointmentsMap).forEach(studentId => {
            studentAppointmentsMap[studentId].completed.sort((a, b) =>
                new Date(b.date_time) - new Date(a.date_time)
            );
            studentAppointmentsMap[studentId].upcoming.sort((a, b) =>
                new Date(a.date_time) - new Date(b.date_time)
            );
            // Sort completed lessons by date (most recent first)
            studentStatsMap[studentId].completedLessons.sort((a, b) =>
                new Date(b.date) - new Date(a.date)
            );
        });

        // Scroll handler for student cards
        const scrollToStudent = (studentId, program = null) => {
            // When viewing 'both', we need to scroll to the correct section based on program
            let element = null;
            if (hasBothPortals && actuallyHasBothPrograms && true && program) {
                // In 'both' view, find the section with the matching program
                const allSections = document.querySelectorAll(`[id^="student-section-${studentId}"]`);
                // Look for the section that contains the program label
                allSections.forEach(section => {
                    const heading = section.querySelector('h2');
                    if (heading && heading.textContent.includes(program)) {
                        element = section;
                    }
                });
                // Fallback: if not found, try to find by program-specific class or structure
                if (!element && allSections.length > 0) {
                    // Check if it's in MathPlex or ReadingPlex section
                    const parentSection = allSections[0].closest('[class*="mb-8"]');
                    if (parentSection) {
                        const sectionHeading = parentSection.querySelector('h2');
                        if (sectionHeading) {
                            if (program === 'MathPlex' && sectionHeading.textContent.includes('MathPlex')) {
                                element = allSections[0];
                            } else if (program === 'ReadingPlex' && sectionHeading.textContent.includes('ReadingPlex')) {
                                element = allSections[0];
                            }
                        }
                    }
                }
            } else {
                // For single program views or when program not specified, use the first matching section
                element = document.getElementById(`student-section-${studentId}`);
            }

            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                // Add a highlight effect
                const ringColor = program === 'ReadingPlex' ? 'ring-mpTeal' : 'ring-mpBlue';
                element.classList.add('ring-2', ringColor, 'ring-opacity-50');
                setTimeout(() => {
                    element.classList.remove('ring-2', ringColor, 'ring-opacity-50');
                }, 2000);
            }
        };

        return (
            <div className="page-box">
                <BrowserTabTitle>{pageTitle}</BrowserTabTitle>
                {loading && <LoadingOverlay />}

                <div className="card w-full mb-6">
                    <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-4">
                        <div className="flex-1">
                            <h2 className="mb-2 text-lg md:text-xl">
                                Welcome to MathPlex & ReadingPlex Member Portal
                            </h2>
                            <p className="text-gray-600 text-xs md:text-sm">
                                {hasBothPortals
                                    ? 'View your children\'s progress and upcoming appointments. Use the toggle to switch between programs.'
                                    : 'View your children\'s progress and upcoming appointments below.'}
                            </p>
                        </div>
                        {hasBothPortals && (
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold text-gray-700">View:</span>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setSelectedProgramView('mathplex')}
                                        className={`px-3 py-1.5 text-xs font-bold rounded transition-all ${selectedProgramView === 'mathplex'
                                            ? 'bg-mpBlue text-white shadow-md'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                            }`}
                                    >
                                        MathPlex
                                    </button>
                                    <button
                                        onClick={() => setSelectedProgramView('readingplex')}
                                        className={`px-3 py-1.5 text-xs font-bold rounded transition-all ${selectedProgramView === 'readingplex'
                                            ? 'bg-mpTeal text-white shadow-md'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                            }`}
                                    >
                                        ReadingPlex
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {apiError && (
                    <div className="card w-full mb-6 bg-red-50 border border-red-200">
                        <p className="text-red-600">{apiError}</p>
                    </div>
                )}

                {/* Compact Payment Buttons - Simple and working */}
                <div className="flex gap-3 mb-4">
                    <OverduePayments compact={true} />
                    <PaymentHistory compact={true} />
                </div>

                {/* Student Information Cards */}
                {filteredStudents.length > 0 && (
                    <div className="card w-full mb-6">
                        <h2 className="mb-4">Your Students</h2>
                        {(hasBothPortals && actuallyHasBothPrograms) || true ? (
                            // When viewing 'both', show ALL unique students once (deduplicated by user_id)
                            // Group by program: MathPlex students first, then ReadingPlex students
                            <>
                                {/* MathPlex Students */}
                                {filteredStudents.filter(s => s.program === 'MathPlex').length > 0 && (
                                    <div className="mb-6">
                                        <h3 className="text-lg font-semibold mb-3 text-mpBlue border-b-2 border-mpBlue pb-2">
                                            📐 MathPlex Students
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {(() => {
                                                // Deduplicate MathPlex students by user_id
                                                const seenIds = new Set();
                                                return filteredStudents.filter(student => {
                                                    if (student.program !== 'MathPlex') return false;
                                                    if (seenIds.has(student.user_id)) {
                                                        return false;
                                                    }
                                                    seenIds.add(student.user_id);
                                                    return true;
                                                }).map((student) => {
                                                    const studentName = `${student.first_name} ${student.last_name}`;
                                                    const studentId = student.user_id;
                                                    const stats = studentStatsMap[studentId] || { totalPages: 0, totalLessons: 0, totalStamps: 0, completedLessons: [] };
                                                    const studentStamps = student.mp_current_stamps || 0;
                                                    const totalCards = student.mp_total_cards || 0;
                                                    const { completed, upcoming } = studentAppointmentsMap[studentId] || { completed: [], upcoming: [] };
                                                    // Check if student is also in ReadingPlex
                                                    const alsoInReadingPlex = filteredStudents.some(s => s.user_id === studentId && s.program === 'ReadingPlex');

                                                    return (
                                                        <div
                                                            key={`mathplex-${studentId}`}
                                                            className="border-2 border-mpBlue rounded-lg bg-white hover:shadow-lg transition-shadow"
                                                        >
                                                            {/* Clickable top half - scrolls to Recent Completed Sessions */}
                                                            <div
                                                                className="p-4 cursor-pointer hover:bg-blue-50 transition-colors rounded-t-lg"
                                                                onClick={() => scrollToStudent(studentId, 'MathPlex')}
                                                            >
                                                                <h3 className="text-lg font-semibold mb-3 text-mpBlue">
                                                                    {studentName}
                                                                    {student.program && <span className="text-xs ml-2 text-gray-700">(MathPlex)</span>}
                                                                </h3>
                                                                <div className="space-y-2 text-sm mb-3">
                                                                    {student.grade && (
                                                                        <div className="text-xs text-gray-600">
                                                                            <span className="font-medium">Grade:</span> {student.grade}
                                                                            {student.school && ` • ${student.school}`}
                                                                        </div>
                                                                    )}
                                                                    {stats.totalPages > 0 && (
                                                                        <div className="text-xs text-gray-700">
                                                                            <span className="font-medium">Pages Completed:</span> {stats.totalPages}
                                                                        </div>
                                                                    )}
                                                                    {stats.totalLessons > 0 && (
                                                                        <div className="text-xs text-gray-700">
                                                                            <span className="font-medium">Lessons Completed:</span> {stats.totalLessons}
                                                                        </div>
                                                                    )}
                                                                    {(stats.totalStamps > 0 || studentStamps > 0 || totalCards > 0) && (
                                                                        <div className="text-xs text-gray-700">
                                                                            <span className="font-medium">Stamps:</span>{' '}
                                                                            {stats.totalStamps > 0 && `Earned: ${stats.totalStamps}`}
                                                                            {stats.totalStamps > 0 && (studentStamps > 0 || totalCards > 0) && ' • '}
                                                                            {studentStamps > 0 && `Total: ${studentStamps}`}
                                                                            {studentStamps > 0 && totalCards > 0 && ' • '}
                                                                            {totalCards > 0 && `Used: ${totalCards}`}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Learning Plans inside card */}
                                                            {studentLearningPlans[studentId] && (
                                                                <div className="px-4 pb-2 pt-3 border-t border-gray-200">
                                                                    <LearningPlanDisplay
                                                                        studentId={studentId}
                                                                        learningPlansData={studentLearningPlans[studentId]}
                                                                        studentName={studentName}
                                                                    />
                                                                </div>
                                                            )}

                                                            {/* Upcoming Appointments inside card */}
                                                            {upcoming && upcoming.length > 0 && (
                                                                <div className="px-4 pb-4 pt-2 border-t border-gray-200">
                                                                    <h4 className="text-sm font-bold text-mpBlue mb-2">📅 Upcoming Appointments</h4>
                                                                    <div className="space-y-2">
                                                                        {upcoming.slice(0, 3).map((apt) => {
                                                                            const date = new Date(apt.date_time);
                                                                            return (
                                                                                <div key={apt.id} className="border-l-4 border-mpBlue pl-3 py-2 bg-blue-50 rounded-r text-xs">
                                                                                    <div className="flex justify-between items-start mb-1">
                                                                                        <div className="font-semibold text-gray-900">
                                                                                            {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                                                                                        </div>
                                                                                        <div className="text-mpBlue font-medium">
                                                                                            {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="text-gray-600">
                                                                                        {apt.mp_center_name} • {apt.duration} min
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                        {upcoming.length > 3 && (
                                                                            <div className="text-xs text-gray-500 text-center pt-1">
                                                                                +{upcoming.length - 3} more appointments
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                });
                                            })()}
                                        </div>
                                    </div>
                                )}

                                {/* ReadingPlex Students */}
                                {filteredStudents.filter(s => s.program === 'ReadingPlex' || (isReadingPlex && !hasBothPortals && !s.program)).length > 0 && (
                                    <div>
                                        <h3 className="text-lg font-semibold mb-3 text-mpTeal border-b-2 border-mpTeal pb-2">
                                            📚 ReadingPlex Students
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {(() => {
                                                // Deduplicate ReadingPlex students by user_id
                                                // Also include students without program tag (single portal mode for ReadingPlex)
                                                const seenIds = new Set();
                                                return filteredStudents.filter(student => {
                                                    // Include ReadingPlex students OR students without program tag (single portal ReadingPlex)
                                                    if (student.program && student.program !== 'ReadingPlex') return false;
                                                    if (seenIds.has(student.user_id)) {
                                                        return false;
                                                    }
                                                    seenIds.add(student.user_id);
                                                    return true;
                                                }).map((student) => {
                                                    const studentName = `${student.first_name} ${student.last_name}`;
                                                    const studentId = student.user_id;
                                                    const { completed, upcoming } = studentAppointmentsMap[studentId] || { completed: [], upcoming: [] };

                                                    return (
                                                        <div
                                                            key={`readingplex-${studentId}`}
                                                            className="border-2 border-mpTeal rounded-lg bg-white hover:shadow-lg transition-shadow"
                                                        >
                                                            {/* Clickable top half - scrolls to Recent Completed Sessions */}
                                                            <div
                                                                className="p-4 cursor-pointer hover:bg-teal-50 transition-colors rounded-t-lg"
                                                                onClick={() => scrollToStudent(studentId, 'ReadingPlex')}
                                                            >
                                                                <h3 className="text-lg font-semibold mb-3 text-mpTeal">
                                                                    {studentName}
                                                                    {student.program && <span className="text-xs ml-2 text-gray-700">(ReadingPlex)</span>}
                                                                </h3>
                                                                <div className="space-y-2 text-sm">
                                                                    {student.grade && (
                                                                        <div className="text-xs text-gray-600">
                                                                            <span className="font-medium">Grade:</span> {student.grade}
                                                                            {student.school && ` • ${student.school}`}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Book Lists inside card */}
                                                            <div className="px-4 pb-2 pt-3 border-t border-gray-200">
                                                                <BookListDisplay
                                                                    studentId={studentId}
                                                                    booksData={readingPlexData.books}
                                                                    bookListsData={readingPlexData.bookLists}
                                                                    bookListItemsData={readingPlexData.bookListItems}
                                                                    studentName={studentName}
                                                                />
                                                            </div>

                                                            {/* Upcoming Appointments inside card */}
                                                            {upcoming && upcoming.length > 0 && (
                                                                <div className="px-4 pb-4 pt-2 border-t border-gray-200">
                                                                    <h4 className="text-sm font-bold text-mpTeal mb-2">📅 Upcoming Appointments</h4>
                                                                    <div className="space-y-2">
                                                                        {upcoming.slice(0, 3).map((apt) => {
                                                                            const date = new Date(apt.date_time);
                                                                            return (
                                                                                <div key={apt.id} className="border-l-4 border-mpTeal pl-3 py-2 bg-teal-50 rounded-r text-xs">
                                                                                    <div className="flex justify-between items-start mb-1">
                                                                                        <div className="font-semibold text-gray-900">
                                                                                            {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                                                                                        </div>
                                                                                        <div className="text-mpTeal font-medium">
                                                                                            {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="text-gray-600">
                                                                                        {apt.rp_center_name} • {apt.duration} min
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                        {upcoming.length > 3 && (
                                                                            <div className="text-xs text-gray-500 text-center pt-1">
                                                                                +{upcoming.length - 3} more appointments
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                });
                                            })()}
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : null}
                    </div>
                )}

                {/* Student-specific sections */}
                {hasBothPortals && actuallyHasBothPrograms ? (
                    <div>
                        {/* MathPlex Students Sections */}
                        {filteredStudents.some(s => s.program === 'MathPlex') && (
                            <div className="mb-8">
                                <h2 className="text-2xl font-bold mb-4 text-mpBlue border-b-2 border-mpBlue pb-2">
                                    📐 MathPlex Students Progress
                                </h2>
                                {filteredStudents.filter(s => s.program === 'MathPlex').map((student) => {
                                    const studentId = student.user_id;
                                    const studentName = studentNameMap[studentId];
                                    const { completed, upcoming } = studentAppointmentsMap[studentId] || { completed: [], upcoming: [] };
                                    const stats = studentStatsMap[studentId] || { totalPages: 0, totalLessons: 0, totalStamps: 0, completedLessons: [] };
                                    const isShowingLessons = showAllLessons[studentId] || false;
                                    const studentProgram = 'MathPlex';

                                    return (
                                        <div key={studentId} id={`student-section-${studentId}`} className="card w-full mb-6 scroll-mt-4">
                                            <h2 className="mb-4 text-xl">{studentName}'s Progress <span className="text-sm text-mpBlue">(MathPlex)</span></h2>


                                            {completed.length > 0 && (
                                                <div className="mb-4">
                                                    <Button
                                                        color="lte-mpTeal"
                                                        onClick={() => setShowAllLessons(prev => ({ ...prev, [studentId]: !prev[studentId] }))}
                                                    >
                                                        {isShowingLessons ? 'Hide' : 'View'} All Completed Sessions ({completed.length})
                                                    </Button>
                                                </div>
                                            )}

                                            {isShowingLessons && completed.length > 0 && (
                                                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                                    <h3 className="text-lg font-semibold mb-3 text-mpBlue">All Completed Sessions</h3>
                                                    <div className="space-y-4 max-h-96 overflow-y-auto">
                                                        {completed.map((apt) => {
                                                            const date = new Date(apt.date_time);
                                                            return (
                                                                <div key={apt.id} className="p-3 bg-white rounded border-l-4 border-mpBlue">
                                                                    <div className="font-semibold text-sm mb-2">
                                                                        {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', weekday: 'short' })} at {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                                                    </div>
                                                                    <div className="text-xs text-gray-700 mb-2">
                                                                        Center {apt.center} • {apt.duration} min
                                                                        {apt.n_pages && ` • ${apt.n_pages} pages`}
                                                                        {apt.n_lessons && parseInt(apt.n_lessons) > 0 && `, ${apt.n_lessons} lesson${parseInt(apt.n_lessons) !== 1 ? 's' : ''}`}
                                                                        {apt.stamps_given && ` • ${apt.stamps_given} stamps`}
                                                                    </div>
                                                                    {apt.session_notes && (
                                                                        <div className="text-xs text-gray-700 mt-2">
                                                                            <span className="font-medium">Session Notes:</span> <span className="text-gray-600">{apt.session_notes}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {completed.length > 0 && (
                                                <div className="mb-6">
                                                    <h3 className="text-lg font-semibold mb-3 text-mpGreen">Recent Completed Sessions</h3>
                                                    <div className="space-y-4">
                                                        {completed.slice(0, 10).map((apt) => {
                                                            const date = new Date(apt.date_time);
                                                            return (
                                                                <div key={apt.id} className="border-l-4 border-mpGreen pl-4 py-2 bg-gray-50 rounded-r">
                                                                    <div className="flex justify-between items-start mb-2">
                                                                        <div>
                                                                            <div className="font-semibold text-base">
                                                                                {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', weekday: 'short' })} at {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                                                            </div>
                                                                            <div className="text-xs text-gray-700 mt-1">
                                                                                Center {apt.center} • {apt.duration} min
                                                                                {apt.n_pages && (<> {` • ${apt.n_pages} pages`} {apt.n_lessons && parseInt(apt.n_lessons) > 0 && `, ${apt.n_lessons} lesson${parseInt(apt.n_lessons) !== 1 ? 's' : ''}`}</>)}
                                                                                {apt.stamps_given && ` • ${apt.stamps_given} stamps`}
                                                                            </div>
                                                                        </div>
                                                                        <span className="text-xs text-mpGreen font-medium">{apt.status}</span>
                                                                    </div>
                                                                    {apt.session_notes && (
                                                                        <div className="mt-2">
                                                                            <div className="text-xs font-medium text-gray-600 mb-1">Session Notes:</div>
                                                                            <div className="text-sm text-gray-800 whitespace-pre-wrap">{apt.session_notes}</div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {upcoming.length > 0 && (
                                                <div>
                                                    <h3 className="text-lg font-semibold mb-3 text-mpBlue">Upcoming Appointments</h3>
                                                    <div className="space-y-2">
                                                        {upcoming.map((apt) => {
                                                            const date = new Date(apt.date_time);
                                                            return (
                                                                <div key={apt.id} className="border-l-4 border-mpBlue pl-4 py-2 bg-blue-50 rounded-r">
                                                                    <div className="flex justify-between items-start">
                                                                        <div className="flex-1">
                                                                            <div className="font-medium text-sm">
                                                                                {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', weekday: 'short' })} at {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                                                            </div>
                                                                            <div className="text-xs text-gray-700 mt-1">Center {apt.center} • {apt.duration} min</div>
                                                                        </div>
                                                                        <span className="text-xs text-mpBlue font-medium ml-2">{apt.status}</span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {completed.length === 0 && upcoming.length === 0 && (
                                                <p className="text-gray-700 text-sm">No appointments found for {studentName}.</p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* ReadingPlex Students Sections */}
                        {filteredStudents.filter(s => s.program === 'ReadingPlex').length > 0 && (
                            <div className="mb-8">
                                <h2 className="text-2xl font-bold mb-4 text-mpTeal border-b-2 border-mpTeal pb-2">
                                    📚 ReadingPlex Students Progress
                                </h2>
                                {filteredStudents.filter(s => s.program === 'ReadingPlex').map((student) => {
                                    const studentId = student.user_id;
                                    const studentName = studentNameMap[studentId];
                                    const { completed, upcoming } = studentAppointmentsMap[studentId] || { completed: [], upcoming: [] };
                                    const stats = studentStatsMap[studentId] || { totalPages: 0, totalLessons: 0, totalStamps: 0, completedLessons: [] };
                                    const isShowingLessons = showAllLessons[studentId] || false;
                                    const studentProgram = 'ReadingPlex';

                                    return (
                                        <div key={studentId} id={`student-section-${studentId}`} className="card w-full mb-6 scroll-mt-4">
                                            <h2 className="mb-4 text-xl">{studentName}'s Progress <span className="text-sm text-mpTeal">(ReadingPlex)</span></h2>

                                            {completed.length > 0 && (
                                                <div className="mb-4">
                                                    <Button
                                                        color="lte-mpTeal"
                                                        onClick={() => setShowAllLessons(prev => ({ ...prev, [studentId]: !prev[studentId] }))}
                                                    >
                                                        {isShowingLessons ? 'Hide' : 'View'} All Completed Sessions ({completed.length})
                                                    </Button>
                                                </div>
                                            )}

                                            {isShowingLessons && completed.length > 0 && (
                                                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                                    <h3 className="text-lg font-semibold mb-3 text-mpBlue">All Completed Sessions</h3>
                                                    <div className="space-y-4 max-h-96 overflow-y-auto">
                                                        {completed.map((apt) => {
                                                            const date = new Date(apt.date_time);
                                                            return (
                                                                <div key={apt.id} className="p-3 bg-white rounded border-l-4 border-mpTeal">
                                                                    <div className="font-semibold text-sm mb-2">
                                                                        {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', weekday: 'short' })} at {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                                                    </div>
                                                                    <div className="text-xs text-gray-700 mb-2">
                                                                        Center {apt.center} • {apt.duration} min
                                                                    </div>
                                                                    {apt.session_notes && (
                                                                        <div className="text-xs text-gray-700 mt-2">
                                                                            <span className="font-medium">Session Notes:</span> <span className="text-gray-600">{apt.session_notes}</span>
                                                                        </div>
                                                                    )}
                                                                    {apt.homework_notes && (
                                                                        <div className="text-xs text-gray-700 mt-2">
                                                                            <span className="font-medium">Homework:</span> <span className="text-gray-600">{apt.homework_notes}</span>
                                                                        </div>
                                                                    )}
                                                                    {(apt.participation || apt.answer_accuracy || apt.depth_of_thinking) && (
                                                                        <div className="text-xs text-gray-700 mt-1">
                                                                            {apt.participation && `Participation: ${apt.participation} • `}
                                                                            {apt.answer_accuracy && `Accuracy: ${apt.answer_accuracy} • `}
                                                                            {apt.depth_of_thinking && `Depth: ${apt.depth_of_thinking}`}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {completed.length > 0 && (
                                                <div className="mb-6">
                                                    <h3 className="text-lg font-semibold mb-3 text-mpGreen">Recent Completed Sessions</h3>
                                                    <div className="space-y-4">
                                                        {completed.slice(0, 10).map((apt) => {
                                                            const date = new Date(apt.date_time);
                                                            return (
                                                                <div key={apt.id} className="border-l-4 border-mpGreen pl-4 py-2 bg-gray-50 rounded-r">
                                                                    <div className="flex justify-between items-start mb-2">
                                                                        <div>
                                                                            <div className="font-semibold text-base">
                                                                                {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', weekday: 'short' })} at {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                                                            </div>
                                                                            <div className="text-xs text-gray-700 mt-1">Center {apt.center} • {apt.duration} min</div>
                                                                        </div>
                                                                        <span className="text-xs text-mpGreen font-medium">{apt.status}</span>
                                                                    </div>
                                                                    {apt.session_notes && (
                                                                        <div className="mt-2 p-2 bg-blue-50 rounded border-l-2 border-mpBlue">
                                                                            <div className="text-xs text-gray-700 mb-1">
                                                                                <span className="font-medium">Activities:</span> <span className="text-gray-600 whitespace-pre-wrap">{apt.session_notes}</span>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {apt.homework_notes && (
                                                                        <div className="mt-2 p-2 bg-blue-50 rounded border-l-2 border-mpBlue">
                                                                            <div className="text-xs text-gray-700 mb-1">
                                                                                <span className="font-medium">Homework:</span> <span className="text-gray-600 whitespace-pre-wrap">{apt.homework_notes}</span>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {(apt.participation || apt.answer_accuracy || apt.depth_of_thinking) && (
                                                                        <div className="text-xs text-gray-700 mt-1">
                                                                            {apt.participation && `Participation: ${apt.participation} • `}
                                                                            {apt.answer_accuracy && `Accuracy: ${apt.answer_accuracy} • `}
                                                                            {apt.depth_of_thinking && `Depth: ${apt.depth_of_thinking}`}
                                                                        </div>
                                                                    )}
                                                                    {apt.session_notes && (
                                                                        <div className="mt-2">
                                                                            <div className="text-xs font-medium text-gray-600 mb-1">Session Notes:</div>
                                                                            <div className="text-sm text-gray-800 whitespace-pre-wrap">{apt.session_notes}</div>
                                                                        </div>
                                                                    )}
                                                                    {apt.homework_notes && (
                                                                        <div className="mt-2">
                                                                            <div className="text-xs font-medium text-gray-600 mb-1">Homework Notes:</div>
                                                                            <div className="text-sm text-gray-800 whitespace-pre-wrap">{apt.homework_notes}</div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {upcoming.length > 0 && (
                                                <div>
                                                    <h3 className="text-lg font-semibold mb-3 text-mpBlue">Upcoming Appointments</h3>
                                                    <div className="space-y-2">
                                                        {upcoming.map((apt) => {
                                                            const date = new Date(apt.date_time);
                                                            let upcomingBook = null;
                                                            let upcomingHomework = null;
                                                            if (apt.session_notes) {
                                                                const bookMatch = apt.session_notes.match(/(?:book|reading|title)[:]\s*([^\n,\.]+)/i);
                                                                if (bookMatch) upcomingBook = bookMatch[1].trim();
                                                            }
                                                            if (apt.homework_notes) {
                                                                upcomingHomework = apt.homework_notes;
                                                            }
                                                            return (
                                                                <div key={apt.id} className="border-l-4 border-mpBlue pl-4 py-2 bg-blue-50 rounded-r">
                                                                    <div className="flex justify-between items-start">
                                                                        <div className="flex-1">
                                                                            <div className="font-medium text-sm">
                                                                                {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', weekday: 'short' })} at {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                                                            </div>
                                                                            <div className="text-xs text-gray-700 mt-1">Center {apt.center} • {apt.duration} min</div>
                                                                            {(upcomingBook || upcomingHomework) && (
                                                                                <div className="mt-2 p-2 bg-white rounded border-l-2 border-mpBlue">
                                                                                    {upcomingBook && (
                                                                                        <div className="text-xs text-gray-700 mb-1">
                                                                                            <span className="font-medium">Book:</span> <span className="text-gray-600">{upcomingBook}</span>
                                                                                        </div>
                                                                                    )}
                                                                                    {upcomingHomework && (
                                                                                        <div className="text-xs text-gray-700">
                                                                                            <span className="font-medium">Homework:</span> <span className="text-gray-600 whitespace-pre-wrap">{upcomingHomework}</span>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <span className="text-xs text-mpBlue font-medium ml-2">{apt.status}</span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {completed.length === 0 && upcoming.length === 0 && (
                                                <p className="text-gray-700 text-sm">No appointments found for {studentName}.</p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                ) : false ? (
                    <div key="progress-mathplex">
                        {(() => {
                            // Deduplicate students by user_id to avoid showing same student twice
                            const seenIds = new Set();
                            return filteredStudents.filter(student => {
                                if (seenIds.has(student.user_id)) {
                                    return false;
                                }
                                seenIds.add(student.user_id);
                                return true;
                            }).map((student) => {
                                const studentId = student.user_id;
                                const studentName = studentNameMap[studentId];
                                const { completed, upcoming } = studentAppointmentsMap[studentId] || { completed: [], upcoming: [] };
                                const stats = studentStatsMap[studentId] || { totalPages: 0, totalLessons: 0, totalStamps: 0, completedLessons: [] };
                                const isShowingLessons = showAllLessons[studentId] || false;

                                return (
                                    <div key={studentId} id={`student-section-${studentId}`} className="card w-full mb-6 scroll-mt-4">
                                        <h2 className="mb-4 text-xl">{studentName}'s Progress <span className="text-sm text-mpBlue">(MathPlex)</span></h2>


                                        {completed.length > 0 && (
                                            <div className="mb-4">
                                                <Button
                                                    color="lte-mpTeal"
                                                    onClick={() => setShowAllLessons(prev => ({ ...prev, [studentId]: !prev[studentId] }))}
                                                >
                                                    {isShowingLessons ? 'Hide' : 'View'} All Completed Sessions ({completed.length})
                                                </Button>
                                            </div>
                                        )}
                                        {isShowingLessons && completed.length > 0 && (
                                            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                                <h3 className="text-lg font-semibold mb-3 text-mpBlue">All Completed Sessions</h3>
                                                <div className="space-y-4 max-h-96 overflow-y-auto">
                                                    {completed.map((apt) => {
                                                        const date = new Date(apt.date_time);
                                                        return (
                                                            <div key={apt.id} className="p-3 bg-white rounded border-l-4 border-mpBlue">
                                                                <div className="font-semibold text-sm mb-2">
                                                                    {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', weekday: 'short' })} at {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                                                </div>
                                                                <div className="text-xs text-gray-700 mb-2">
                                                                    Center {apt.center} • {apt.duration} min
                                                                    {apt.n_pages && ` • ${apt.n_pages} pages`}
                                                                    {apt.n_lessons && parseInt(apt.n_lessons) > 0 && `, ${apt.n_lessons} lesson${parseInt(apt.n_lessons) !== 1 ? 's' : ''}`}
                                                                    {apt.stamps_given && ` • ${apt.stamps_given} stamps`}
                                                                </div>
                                                                {apt.session_notes && (
                                                                    <div className="text-xs text-gray-700 mt-2">
                                                                        <span className="font-medium">Session Notes:</span> <span className="text-gray-600">{apt.session_notes}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                        {completed.length > 0 && (
                                            <div className="mb-6">
                                                <h3 className="text-lg font-semibold mb-3 text-mpGreen">Recent Completed Sessions</h3>
                                                <div className="space-y-4">
                                                    {completed.slice(0, 10).map((apt) => {
                                                        const date = new Date(apt.date_time);
                                                        return (
                                                            <div key={apt.id} className="border-l-4 border-mpGreen pl-4 py-2 bg-gray-50 rounded-r">
                                                                <div className="flex justify-between items-start mb-2">
                                                                    <div>
                                                                        <div className="font-semibold text-base">
                                                                            {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', weekday: 'short' })} at {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                                                        </div>
                                                                        <div className="text-xs text-gray-700 mt-1">
                                                                            Center {apt.center} • {apt.duration} min
                                                                            {apt.n_pages && (<> {` • ${apt.n_pages} pages`} {apt.n_lessons && parseInt(apt.n_lessons) > 0 && `, ${apt.n_lessons} lesson${parseInt(apt.n_lessons) !== 1 ? 's' : ''}`}</>)}
                                                                            {apt.stamps_given && ` • ${apt.stamps_given} stamps`}
                                                                        </div>
                                                                    </div>
                                                                    <span className="text-xs text-mpGreen font-medium">{apt.status}</span>
                                                                </div>
                                                                {apt.session_notes && (
                                                                    <div className="mt-2">
                                                                        <div className="text-xs font-medium text-gray-600 mb-1">Session Notes:</div>
                                                                        <div className="text-sm text-gray-800 whitespace-pre-wrap">{apt.session_notes}</div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                        {upcoming.length > 0 && (
                                            <div>
                                                <h3 className="text-lg font-semibold mb-3 text-mpBlue">Upcoming Appointments</h3>
                                                <div className="space-y-2">
                                                    {upcoming.map((apt) => {
                                                        const date = new Date(apt.date_time);
                                                        return (
                                                            <div key={apt.id} className="border-l-4 border-mpBlue pl-4 py-2 bg-blue-50 rounded-r">
                                                                <div className="flex justify-between items-start">
                                                                    <div className="flex-1">
                                                                        <div className="font-medium text-sm">
                                                                            {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', weekday: 'short' })} at {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                                                        </div>
                                                                        <div className="text-xs text-gray-700 mt-1">Center {apt.center} • {apt.duration} min</div>
                                                                    </div>
                                                                    <span className="text-xs text-mpBlue font-medium ml-2">{apt.status}</span>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                        {completed.length === 0 && upcoming.length === 0 && (
                                            <p className="text-gray-700 text-sm">No appointments found for {studentName}.</p>
                                        )}
                                    </div>
                                );
                            });
                        })()}
                    </div>
                ) : false ? (
                    <div key="progress-readingplex">
                        {(() => {
                            // Deduplicate students by user_id to avoid showing same student twice
                            const seenIds = new Set();
                            return filteredStudents.filter(student => {
                                if (seenIds.has(student.user_id)) {
                                    return false;
                                }
                                seenIds.add(student.user_id);
                                return true;
                            }).map((student) => {
                                const studentId = student.user_id;
                                const studentName = studentNameMap[studentId];
                                const { completed, upcoming } = studentAppointmentsMap[studentId] || { completed: [], upcoming: [] };
                                const isShowingLessons = showAllLessons[studentId] || false;

                                return (
                                    <div key={studentId} id={`student-section-${studentId}`} className="card w-full mb-6 scroll-mt-4">
                                        <h2 className="mb-4 text-xl">{studentName}'s Progress <span className="text-sm text-mpTeal">(ReadingPlex)</span></h2>
                                        {completed.length > 0 && (
                                            <div className="mb-4">
                                                <Button
                                                    color="lte-mpTeal"
                                                    onClick={() => setShowAllLessons(prev => ({ ...prev, [studentId]: !prev[studentId] }))}
                                                >
                                                    {isShowingLessons ? 'Hide' : 'View'} All Completed Sessions ({completed.length})
                                                </Button>
                                            </div>
                                        )}
                                        {isShowingLessons && completed.length > 0 && (
                                            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                                <h3 className="text-lg font-semibold mb-3 text-mpBlue">All Completed Sessions</h3>
                                                <div className="space-y-4 max-h-96 overflow-y-auto">
                                                    {completed.map((apt) => {
                                                        const date = new Date(apt.date_time);
                                                        return (
                                                            <div key={apt.id} className="p-3 bg-white rounded border-l-4 border-mpTeal">
                                                                <div className="font-semibold text-sm mb-2">
                                                                    {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', weekday: 'short' })} at {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                                                </div>
                                                                <div className="text-xs text-gray-700 mb-2">
                                                                    Center {apt.center} • {apt.duration} min
                                                                </div>
                                                                {apt.session_notes && (
                                                                    <div className="text-xs text-gray-700 mt-2">
                                                                        <span className="font-medium">Session Notes:</span> <span className="text-gray-600">{apt.session_notes}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                        {completed.length > 0 && (
                                            <div className="mb-6">
                                                <h3 className="text-lg font-semibold mb-3 text-mpGreen">Recent Completed Sessions</h3>
                                                <div className="space-y-4">
                                                    {completed.slice(0, 10).map((apt) => {
                                                        const date = new Date(apt.date_time);
                                                        return (
                                                            <div key={apt.id} className="border-l-4 border-mpGreen pl-4 py-2 bg-gray-50 rounded-r">
                                                                <div className="flex justify-between items-start mb-2">
                                                                    <div>
                                                                        <div className="font-semibold text-base">
                                                                            {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', weekday: 'short' })} at {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                                                        </div>
                                                                        <div className="text-xs text-gray-700 mt-1">Center {apt.center} • {apt.duration} min</div>
                                                                    </div>
                                                                    <span className="text-xs text-mpGreen font-medium">{apt.status}</span>
                                                                </div>
                                                                {apt.session_notes && (
                                                                    <div className="mt-2 p-2 bg-blue-50 rounded border-l-2 border-mpBlue">
                                                                        <div className="text-xs text-gray-700 mb-1">
                                                                            <span className="font-medium">Activities:</span> <span className="text-gray-600 whitespace-pre-wrap">{apt.session_notes}</span>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {apt.homework_notes && (
                                                                    <div className="mt-2 p-2 bg-blue-50 rounded border-l-2 border-mpBlue">
                                                                        <div className="text-xs text-gray-700 mb-1">
                                                                            <span className="font-medium">Homework:</span> <span className="text-gray-600 whitespace-pre-wrap">{apt.homework_notes}</span>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {(apt.participation || apt.answer_accuracy || apt.depth_of_thinking) && (
                                                                    <div className="text-xs text-gray-700 mt-1">
                                                                        {apt.participation && `Participation: ${apt.participation} • `}
                                                                        {apt.answer_accuracy && `Accuracy: ${apt.answer_accuracy} • `}
                                                                        {apt.depth_of_thinking && `Depth: ${apt.depth_of_thinking}`}
                                                                    </div>
                                                                )}
                                                                {apt.session_notes && (
                                                                    <div className="mt-2">
                                                                        <div className="text-xs font-medium text-gray-600 mb-1">Session Notes:</div>
                                                                        <div className="text-sm text-gray-800 whitespace-pre-wrap">{apt.session_notes}</div>
                                                                    </div>
                                                                )}
                                                                {apt.homework_notes && (
                                                                    <div className="mt-2">
                                                                        <div className="text-xs font-medium text-gray-600 mb-1">Homework Notes:</div>
                                                                        <div className="text-sm text-gray-800 whitespace-pre-wrap">{apt.homework_notes}</div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                        {upcoming.length > 0 && (
                                            <div>
                                                <h3 className="text-lg font-semibold mb-3 text-mpBlue">Upcoming Appointments</h3>
                                                <div className="space-y-2">
                                                    {upcoming.map((apt) => {
                                                        const date = new Date(apt.date_time);
                                                        return (
                                                            <div key={apt.id} className="border-l-4 border-mpBlue pl-4 py-2 bg-blue-50 rounded-r">
                                                                <div className="flex justify-between items-start">
                                                                    <div className="flex-1">
                                                                        <div className="font-medium text-sm">
                                                                            {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', weekday: 'short' })} at {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                                                        </div>
                                                                        <div className="text-xs text-gray-700 mt-1">Center {apt.center} • {apt.duration} min</div>
                                                                    </div>
                                                                    <span className="text-xs text-mpBlue font-medium ml-2">{apt.status}</span>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                        {completed.length === 0 && upcoming.length === 0 && (
                                            <p className="text-gray-700 text-sm">No appointments found for {studentName}.</p>
                                        )}
                                    </div>
                                );
                            });
                        })()}
                    </div>
                ) : (
                    <div key="progress-default">
                        {filteredStudents.map((student) => {
                            const studentId = student.user_id;
                            const studentName = studentNameMap[studentId];
                            const { completed, upcoming } = studentAppointmentsMap[studentId] || { completed: [], upcoming: [] };
                            const stats = studentStatsMap[studentId] || { totalPages: 0, totalLessons: 0, totalStamps: 0, completedLessons: [] };
                            const isShowingLessons = showAllLessons[studentId] || false;

                            return (
                                <div key={studentId} id={`student-section-${studentId}`} className="card w-full mb-6 scroll-mt-4">
                                    <h2 className="mb-4 text-xl">{studentName}'s Progress</h2>
                                    {completed.length > 0 && (
                                        <div className="mb-4">
                                            <Button
                                                color="lte-mpTeal"
                                                onClick={() => setShowAllLessons(prev => ({ ...prev, [studentId]: !prev[studentId] }))}
                                            >
                                                {isShowingLessons ? 'Hide' : 'View'} All Completed Sessions ({completed.length})
                                            </Button>
                                        </div>
                                    )}
                                    {isShowingLessons && completed.length > 0 && (
                                        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                            <h3 className="text-lg font-semibold mb-3 text-mpBlue">All Completed Sessions</h3>
                                            <div className="space-y-4 max-h-96 overflow-y-auto">
                                                {completed.map((apt) => {
                                                    const date = new Date(apt.date_time);
                                                    return (
                                                        <div key={apt.id} className="p-3 bg-white rounded border-l-4 border-mpBlue">
                                                            <div className="font-semibold text-sm mb-2">
                                                                {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', weekday: 'short' })} at {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                                            </div>
                                                            <div className="text-xs text-gray-700 mb-2">
                                                                Center {apt.center} • {apt.duration} min
                                                                {apt.n_pages && ` • ${apt.n_pages} pages`}
                                                                {apt.n_lessons && parseInt(apt.n_lessons) > 0 && `, ${apt.n_lessons} lesson${parseInt(apt.n_lessons) !== 1 ? 's' : ''}`}
                                                                {apt.stamps_given && ` • ${apt.stamps_given} stamps`}
                                                            </div>
                                                            {apt.session_notes && (
                                                                <div className="text-xs text-gray-700 mt-2">
                                                                    <span className="font-medium">Session Notes:</span> <span className="text-gray-600">{apt.session_notes}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                    {completed.length > 0 && (
                                        <div className="mb-6">
                                            <h3 className="text-lg font-semibold mb-3 text-mpGreen">Recent Completed Sessions</h3>
                                            <div className="space-y-4">
                                                {completed.slice(0, 10).map((apt) => {
                                                    const date = new Date(apt.date_time);
                                                    return (
                                                        <div key={apt.id} className="border-l-4 border-mpGreen pl-4 py-2 bg-gray-50 rounded-r">
                                                            <div className="flex justify-between items-start mb-2">
                                                                <div>
                                                                    <div className="font-semibold text-base">
                                                                        {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', weekday: 'short' })} at {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                                                    </div>
                                                                    <div className="text-xs text-gray-700 mt-1">
                                                                        Center {apt.center} • {apt.duration} min
                                                                        {apt.n_pages && (<> {` • ${apt.n_pages} pages`} {apt.n_lessons && parseInt(apt.n_lessons) > 0 && `, ${apt.n_lessons} lesson${parseInt(apt.n_lessons) !== 1 ? 's' : ''}`}</>)}
                                                                        {apt.stamps_given && ` • ${apt.stamps_given} stamps`}
                                                                    </div>
                                                                </div>
                                                                <span className="text-xs text-mpGreen font-medium">{apt.status}</span>
                                                            </div>
                                                            {apt.session_notes && (
                                                                <div className="mt-2">
                                                                    <div className="text-xs font-medium text-gray-600 mb-1">Session Notes:</div>
                                                                    <div className="text-sm text-gray-800 whitespace-pre-wrap">{apt.session_notes}</div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                    {upcoming.length > 0 && (
                                        <div>
                                            <h3 className="text-lg font-semibold mb-3 text-mpBlue">Upcoming Appointments</h3>
                                            <div className="space-y-2">
                                                {upcoming.map((apt) => {
                                                    const date = new Date(apt.date_time);
                                                    return (
                                                        <div key={apt.id} className="border-l-4 border-mpBlue pl-4 py-2 bg-blue-50 rounded-r">
                                                            <div className="flex justify-between items-start">
                                                                <div className="flex-1">
                                                                    <div className="font-medium text-sm">
                                                                        {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', weekday: 'short' })} at {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                                                    </div>
                                                                    <div className="text-xs text-gray-700 mt-1">Center {apt.center} • {apt.duration} min</div>
                                                                </div>
                                                                <span className="text-xs text-mpBlue font-medium ml-2">{apt.status}</span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                    {completed.length === 0 && upcoming.length === 0 && (
                                        <p className="text-gray-700 text-sm">No appointments found for {studentName}.</p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {modalMode && (
                    <DetailsModal
                        selectedAppointment={selectedAppointment}
                        onSubmitCallback={onSubmitCallback}
                    />
                )}

                <Socket
                    refreshData={refreshMemberData}
                    page={pageTitle}
                    setVersion={props.setVersion}
                />
            </div>
        );
    }

    // Admin portal view (original)
    return (
        <div className="page-box">
            <BrowserTabTitle>{pageTitle}</BrowserTabTitle>

            {/* Updater Link - Only for MathPlex */}
            {!isReadingPlex && (
                <div className="mb-6 flex justify-end">
                    <Button
                        color="lte-mpTeal"
                        onClick={() => window.location.href = '/upload-answer-keys-1761816613.html'}
                    >
                        Updater
                    </Button>
                </div>
            )}

            {/* <div className="row no-mar">
                <Card style={{ width: "32%", marginRight: "2%" }}>
                    <Card.Body>
                        <Card.Title>
                            <div className="row no-mar" styl={{ display: "flex" }}>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip>
                                            Contracts with overdue payments.
                                        </Tooltip>
                                    }
                                >
                                    <img width="10%" src={toAbsoluteUrl('/media/svg/mathplex/money.svg')} alt="money.svg"/>
                                </OverlayTrigger>&ensp;
                                <h2 style={{ alignSelf: "center" }}>Overdue Payments</h2>
                            </div>
                        </Card.Title>
                        <Card.Text>
                            A list of contracts with payment dates set to today.
                        </Card.Text>
                    </Card.Body>
                </Card>
                <Card style={{ width: "32%", marginRight: "2%" }}>
                    <Card.Body>
                        <Card.Title>
                            <div className="row no-mar" styl={{ display: "flex" }}>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip>
                                            Contracts ending within the specified date range. Set a contract's "warn" property to false to prevent a contract from showing up here.
                                        </Tooltip>
                                    }
                                >
                                    <img width="10%" src={toAbsoluteUrl('/media/svg/mathplex/contract.svg')} alt="contract.svg"/>
                                </OverlayTrigger>&ensp;
                                <h2 style={{ alignSelf: "center" }}>Contract Watch</h2>
                            </div>
                        </Card.Title>
                        <Card.Text>
                            A list of contracts with the warning property set to true that are ending within the next month. Also: active students without an active contract.
                        </Card.Text>
                    </Card.Body>
                </Card>
                <Card style={{ width: '32%' }}>
                    <Card.Body>
                        <Card.Title>
                            <div className="row no-mar" styl={{ display: "flex" }}>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip>
                                            Flags that were created two or more days ago.
                                        </Tooltip>
                                    }
                                >
                                    <img width="10%" src={toAbsoluteUrl('/media/svg/mathplex/flag.svg')} alt="flag.svg"/>
                                </OverlayTrigger>&ensp;
                                <h2 style={{ alignSelf: "center" }}>Outstanding Flags</h2>
                            </div>
                        </Card.Title>
                        <Card.Text>
                            A list of flags created two or more days ago.
                        </Card.Text>
                    </Card.Body>
                </Card>
            </div>

            <br/> */}

            <div className="flex flex-row">
                <div className="card w-full">
                    <div className="flex flex-row w-full">
                        <h2>Version History</h2>

                        <div className="grid grid-cols-1 gap-y-2 w-1/3">
                            <div style={{ marginLeft: "2rem" }}>
                                <SelectSingle
                                    id="home-view-version"
                                    name="viewVersion"
                                    value={viewVersion}
                                    options={versionOptions}
                                    onChange={(e) => setViewVersion(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <br />

                    <VersionHistory
                        version={viewVersion.value}
                    />
                </div>
            </div>

            <Socket
                refreshData={() => null}
                page={pageTitle}
                setVersion={props.setVersion}
            />
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        auth: state.auth
    };
}

export default connect(mapStateToProps, {
    fetchMemberAppointmentsParentStudent,
    fetchMemberAppointmentsUnified,
    fetchMemberStudentsRelevant,
    fetchMemberStudentsUnified,
    fetchMemberLearningPlansStudent
})(Home);